import { create } from 'zustand';
import axios from 'axios';
import { DYNAMIC_BOOKING_SECTION_API_URL } from 'Constants';

/** Backend DTO shape for a dynamic booking section. */
export interface DynamicBookingSectionDto {
	id: string;
	label: string;
	labelKey: string | null;
	description: string | null;
	descriptionKey: string | null;
	icon: string | null;
	colour: string | null;
	order: number;
	archived: boolean;
}

interface DynamicBookingSectionsState {
	sections: DynamicBookingSectionDto[];
	loaded: boolean;
	loading: boolean;
	error: string | null;
	loadSections: (force?: boolean) => Promise<void>;
	invalidate: () => void;
}

/**
 * Caches dynamic-section definitions for the wizard grouping and the admin field
 * form's section picker. Loaded once on first use; admin saves call `invalidate()`
 * so the next read refetches.
 */
export const useDynamicBookingSectionsStore = create<DynamicBookingSectionsState>((set, get) => ({
	sections: [],
	loaded: false,
	loading: false,
	error: null,

	loadSections: async (force = false) => {
		const state = get();
		if (!force && (state.loaded || state.loading)) return;

		set({ loading: true, error: null });
		try {
			const response = await axios.get(`${DYNAMIC_BOOKING_SECTION_API_URL}/active`);
			const list = (response.data ?? []) as DynamicBookingSectionDto[];
			set({ sections: list, loaded: true, loading: false });
		} catch (e: any) {
			console.error('Failed to load dynamic booking sections', e);
			set({ loading: false, error: e?.message ?? 'Failed to load dynamic sections' });
		}
	},

	invalidate: () => set({ loaded: false }),
}));

/** Lookup a section by id from the cache. */
export function getSectionById(
	sections: DynamicBookingSectionDto[],
	sectionId: string | null | undefined,
): DynamicBookingSectionDto | undefined {
	if (!sectionId) return undefined;
	return sections.find(s => s.id === sectionId);
}
